/**
 * PATH: packages/email-hub/src/feature/components/presetSignaturePlacement.ts
 * FILE_ID: EMAILHUB-PDF-PRESET-PLACEMENT-001
 * PURPOSE: Place a signature and a date line at the bottom right of chosen pages.
 */
import { formatSignatureDate } from './_pdfHelpers'
import type { EditorItem, RenderedPage } from './_pdfTypes'

export const PLACEMENT_RIGHT_MARGIN_PT = 40
export const PLACEMENT_BOTTOM_MARGIN_PT = 40

export function bottomRightPlacement(
  page: RenderedPage | undefined,
  widthPt: number,
  heightPt: number,
): { xPt: number; yPt: number } {
  if (!page) return { xPt: 100, yPt: 600 }
  return {
    xPt: Math.max(0, page.widthPt - widthPt - PLACEMENT_RIGHT_MARGIN_PT),
    yPt: Math.max(0, page.heightPt - heightPt - PLACEMENT_BOTTOM_MARGIN_PT),
  }
}

export interface PresetSignature {
  pngBytes: Uint8Array
  pageIndices: number[]
  /** Placements to restore when the operator reopens a signed copy. */
  items?: EditorItem[]
}

function clonePresetItem(item: EditorItem): EditorItem {
  const id = `${item.id}-reopen-${Math.random().toString(36).slice(2, 7)}`
  if (item.kind !== 'signature-image' && item.kind !== 'signature-drawn') {
    return { ...item, id }
  }
  const imageBytes = new Uint8Array(item.imageBytes)
  return {
    ...item,
    id,
    imageBytes,
    previewUrl: URL.createObjectURL(new Blob([imageBytes], { type: 'image/png' })),
  }
}

export function buildPresetSignatureItems(
  pages: RenderedPage[],
  preset: PresetSignature,
  date = new Date(),
): EditorItem[] {
  const items: EditorItem[] = (preset.items ?? []).map(clonePresetItem)
  for (const pageIndex of preset.pageIndices) {
    const page = pages[pageIndex]
    if (!page) continue
    const rand = Math.random().toString(36).slice(2, 7)
    const dateBox = bottomRightPlacement(page, 130, 20)
    const signatureX = bottomRightPlacement(page, 180, 54).xPt
    const previewUrl = URL.createObjectURL(new Blob([new Uint8Array(preset.pngBytes)], { type: 'image/png' }))
    items.push({
      id: `txt-date-preset-${pageIndex}-${rand}`,
      kind: 'text',
      textRole: 'date',
      text: formatSignatureDate(date),
      fontSizePt: 12,
      pageIndex,
      xPt: dateBox.xPt,
      yPt: dateBox.yPt,
      widthPt: 130,
      heightPt: 20,
    })
    items.push({
      id: `sig-preset-${pageIndex}-${rand}`,
      kind: 'signature-drawn',
      pageIndex,
      xPt: signatureX,
      yPt: Math.max(0, dateBox.yPt - 54 - 4),
      widthPt: 180,
      heightPt: 54,
      imageBytes: preset.pngBytes,
      previewUrl,
      sigSize: 'signature',
    })
  }
  return items
}
